(() => {
  let pending = false;
  const observed = new WeakSet();
  const resizeObserver = new ResizeObserver(queue);
  function queue() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(sync);
  }
  function arrangeProductRows(list) {
    if (list.dataset.akFormatRows === 'true') return;
    const source = [...list.children].filter(cell =>
      !cell.classList.contains('ak-collection-grid-tile') &&
      !cell.classList.contains('ak-collection-placeholder')
    );
    const mobilePlaceholder = list.querySelector('.ak-collection-placeholder:not(.ak-collection-placeholder-desktop)');
    const template = list.querySelector('.ak-collection-grid-tile');
    const rows = [];
    const arthletics = !!list.querySelector('[data-ak-arthletics-media]');
    const gallery = !!list.querySelector('[data-ak-gallery-art]');
    let regular = [];

    // Share Simple's existing wide-intro classification; product handles
    // and the displayed thumbnail's crop must not decide the row width.
    const wide = cell => !!cell?.querySelector('.ak-collection-product-card[data-ak-wide-intro="true"], .ak-collection-framed-art[data-format="landscape"]');
    if (gallery) {
      // Collect each format before planning rows, so scattered square or
      // portrait images do not create isolated runs or mixed-height rows.
      const format = cell => wide(cell) ? 'landscape'
        : cell.querySelector('[data-ak-square-media="true"]') ? 'square' : 'portrait';
      const formatOrder = new Map();
      source.forEach(cell => {
        const key = format(cell);
        if (!formatOrder.has(key)) formatOrder.set(key, formatOrder.size);
      });
      source.sort((a, b) => formatOrder.get(format(a)) - formatOrder.get(format(b)));
    }

    function hatch() {
      if (template) return template.cloneNode(true);
      const cell = document.createElement('li');
      cell.className = 'ak-collection-card-cell ak-collection-grid-tile';
      cell.setAttribute('aria-hidden', 'true');
      cell.setAttribute('role', 'presentation');
      const panel = document.createElement('div');
      panel.className = 'ak-collection-grid-panel';
      ['media', 'caption'].forEach(part => {
        const field = document.createElement('div');
        field.className = `ak-collection-grid-${part}`;
        panel.append(field);
      });
      cell.append(panel);
      return cell;
    }
    function finishRegular() {
      if (!regular.length) return;
      rows.push({cells: regular, wide: false});
      regular = [];
    }
    for (let index = 0; index < source.length; index++) {
      const cell = source[index];
      if (!cell.querySelector('.ak-collection-product-card')) {
        finishRegular();
        rows.push({cells: [cell], utility: true});
      } else if (wide(cell)) {
        finishRegular();
        const cells = [cell];
        if (wide(source[index + 1])) cells.push(source[++index]);
        rows.push({cells, columns: 2, wide: true, hasHatch: cells.length === 1});
      } else {
        // Never let a portrait photograph set the height of square covers.
        const square = !!cell.querySelector('[data-ak-square-media="true"]');
        const previousSquare = regular.length && !!regular[0].querySelector('[data-ak-square-media="true"]');
        if (regular.length && square !== previousSquare) finishRegular();
        regular.push(cell);
      }
    }
    finishRegular();

    // Plan complete runs without two-product portrait rows or edge hatch.
    // Short runs between landscape groups use individual full-width rows.
    const soloWide = row => !!(row?.wide && row.hasHatch);
    const planned = [];
    // Reserve the first two rows before planning the variable composition.
    // Keep each row within one image format, including short collections.
    const rowFormat = row => row.wide ? 'landscape'
      : row.cells[0]?.querySelector('[data-ak-square-media="true"]') ? 'square' : 'portrait';
    function takeOpening(limit, index = 0) {
      if (!rows[index] || rows[index].utility) return null;
      const format = rowFormat(rows[index]);
      const cells = [];
      while (rows[index] && !rows[index].utility && rowFormat(rows[index]) === format && cells.length < limit) {
        const run = rows[index];
        cells.push(...run.cells.splice(0, limit - cells.length));
        if (!run.cells.length) rows.splice(index, 1);
      }
      return {cells, wide:format === 'landscape'};
    }
    const first = takeOpening(2);
    if (first) planned.push({...first, columns:3, hasHatch:true, keepColumns:true, opening:true});
    function groupSize(index) {
      const format = rowFormat(rows[index]);
      let total = 0;
      for (let i = index; rows[i] && !rows[i].utility && rowFormat(rows[i]) === format; i++) total += rows[i].cells.length;
      return total;
    }
    // Prefer a complete same-format second row over a sparse remainder.
    let secondIndex = rows.findIndex((row, index) => !row.utility && groupSize(index) >= 4);
    const second = takeOpening(4, Math.max(0, secondIndex));
    if (second) planned.push({...second, columns:4, hasHatch:second.cells.length < 4,
      hatchSpan:4-second.cells.length, keepColumns:true, silentHatch:true});
    // Choose a fresh composition after the fixed opening per view, excluding the previous
    // opening in this tab. Resizes keep the already-built composition stable.
    let opening = Math.floor(Math.random() * 8);
    try {
      const key = `ak-collection-opening:${location.pathname}`;
      const previous = sessionStorage.getItem(key);
      if (previous !== null) {
        const candidate = Math.floor(Math.random() * 7);
        opening = candidate >= Number(previous) ? candidate + 1 : candidate;
      }
      sessionStorage.setItem(key, String(opening));
    } catch (_) { /* Storage can be unavailable in private browsing. */ }
    let preferred = opening & 1 ? 3 : 4;
    let clearRows = second ? 1 : 0;
    let nextHatchAfter = 1;
    rows.forEach((run, runIndex) => {
      if (run.wide || run.utility) {
        planned.push(run);
        if (run.hasHatch) { clearRows = 0; nextHatchAfter = 1 + Math.floor(Math.random() * 2); }
        else clearRows++;
        return;
      }
      const products = run.cells;
      const firstClear = soloWide(rows[runIndex - 1]);
      const lastClear = soloWide(rows[runIndex + 1]);
      if (products.length < 3) {
        // Short runs retain normal card widths; hatch closes empty fields.
        const cells = products.slice();
        // A lone product keeps a quarter-width card; its three empty
        // quarters merge. Two products use thirds with the empty middle.
        const columns = products.length === 1 ? 4 : 3;
        planned.push({cells, columns, wide:false, hasHatch:true, hatchSpan:columns-products.length, keepColumns:true});
        return;
      }
      const memo = new Map();
      function choices(offset, previousHatch) {
        const remaining = products.length - offset;
        const options = [];
        for (const columns of [3, 4]) {
          for (const hasHatch of [false, true]) {
            const count = columns - Number(hasHatch);
            if (count < 2 || count > remaining || remaining - count === 1) continue;
            if (hasHatch && count > 2 && ((runIndex === 0 && offset === 0) || (runIndex === rows.length - 1 && count === remaining))) continue;
            if (hasHatch && count > 2 && (previousHatch || (offset === 0 && firstClear) || (count === remaining && lastClear))) continue;
            options.push({columns, count, hasHatch});
          }
        }
        return options;
      }
      function canFinish(offset, previousHatch) {
        if (offset === products.length) return true;
        const key = `${offset}:${previousHatch}`;
        if (!memo.has(key)) memo.set(key, choices(offset, previousHatch).some(option =>
          canFinish(offset + option.count, option.hasHatch)
        ));
        return memo.get(key);
      }
      let previousHatch = false;
      for (let offset = 0; offset < products.length;) {
        // A pause follows one or two clear rows, rather than relying on an
        // unbounded coin toss that could leave seven rows without hatch.
        const wantHatch = clearRows >= nextHatchAfter;
        const options = choices(offset, previousHatch)
          .filter(option => canFinish(offset + option.count, option.hasHatch))
          .map(option => ({...option, score: Math.random() +
            (option.columns === preferred ? 1 : 0) +
            (option.hasHatch === wantHatch ? 4 : 0) -
            (option.columns === 2 ? 2 : 0)}))
          .sort((a, b) => b.score - a.score);
        const option = options[0];
        planned.push({cells: products.slice(offset, offset + option.count), columns: option.columns, wide: false, hasHatch: option.hasHatch, keepColumns: option.count < 3});
        offset += option.count;
        previousHatch = option.hasHatch;
        if (option.hasHatch) {
          clearRows = 0;
          nextHatchAfter = 1 + Math.floor(Math.random() * 2);
        } else clearRows++;
        preferred = option.columns === 4 ? 3 : 4;
      }
    });
    [planned[0], planned[planned.length - 1]].forEach(row => {
      // Wide cards always occupy half the list, including an unpaired card
      // at either edge. Its hatch closes the remaining half of that row.
      if (row?.hasHatch && !row.wide && !row.keepColumns) {
        row.hasHatch = false;
        row.columns = row.cells.length;
      }
    });
    // Alternate the side of every hatch, even across intervening full rows.
    // The initial side and regular-row composition vary on each new view.
    let hatchLeft = !!(opening & 4);
    planned.forEach(row => {
      if (!row.hasHatch) return;
      const field=hatch();
      field.dataset.akHatchSpan=String(row.hatchSpan || 1);
      if (row.silentHatch) {
        field.dataset.akSilentGrid = 'true';
        row.cells.push(field);
        return;
      }
      if (row.columns === 3) {
        // Only the middle third centres on the eight-field master grid.
        row.cells.splice(1, 0, field);
        if (row.opening && row.cells.length < 3) {
          const remainder = hatch();
          remainder.dataset.akHatchSpan = '1';
          remainder.dataset.akSilentGrid = 'true';
          row.cells.push(remainder);
        }
      } else {
        if (hatchLeft) row.cells.unshift(field);
        else row.cells.push(field);
        hatchLeft = !hatchLeft;
      }
    });
    const fragment = document.createDocumentFragment();
    let animationIndex = 0;
    planned.forEach((row, rowIndex) => {
      const products = row.cells.filter(cell => cell.querySelector('.ak-collection-product-card'));
      const square = products.length > 0 && products.every(cell =>
        cell.querySelector('[data-ak-square-media="true"]')
      );
      row.cells.forEach((cell, columnIndex) => {
        cell.toggleAttribute('data-ak-square-row', square);
        const hatchSpan=Number(cell.dataset.akHatchSpan || 1);
        if (hatchSpan > 1) {
          cell.style.setProperty('--ak-hatch-tracks',String(60 / row.columns * hatchSpan));
          cell.style.setProperty('--ak-hatch-ratio',String((row.wide ? 1.5 : square ? 1 : .75) * hatchSpan));
        }
        if (!row.utility) {
          cell.dataset.akCardColumns = String(row.columns);
          cell.dataset.akRowWide = String(row.wide);
          cell.dataset.akCollectionRow = String(rowIndex);
          if (columnIndex === 0) cell.dataset.akRowStart = 'true';
          else delete cell.dataset.akRowStart;
          cell.classList.toggle('ak-collection-last-row-desktop', rowIndex === planned.length - 1);
          cell.style.setProperty('--ak-collection-product-index', String(animationIndex++));
        }
        fragment.append(cell);
      });
    });
    if (mobilePlaceholder) fragment.append(mobilePlaceholder);
    list.dataset.akFormatRows = 'true';
    list.replaceChildren(fragment);
  }
  function sync() {
    pending = false;
    const list = document.querySelector('#filterable .collection-list');
    const grid = document.querySelector('.ak-top-collection-desktop__grid, .ak-collection-editorial-divider .ak-vpcompress22-desktop__grid');
    if (!list || !grid) return;
    [list, grid].forEach(element => {
      if (!observed.has(element)) {
        observed.add(element);
        resizeObserver.observe(element);
      }
    });
    const productCells = [...list.children].filter(cell => cell.querySelector('.ak-collection-product-card'));
    if (innerWidth <= 1200) {
      list.style.removeProperty('--ak-collection-tracks');
      if (list.querySelector('[data-ak-square-media="true"]')) {
        let previousFormat = null;
        productCells.forEach(cell => {
          const format = !!cell.querySelector('[data-ak-square-media="true"]');
          cell.style.gridColumnStart = previousFormat !== null && format !== previousFormat ? '1' : '';
          previousFormat = format;
        });
      }
      return;
    }
    productCells.forEach(cell => { cell.style.gridColumnStart = ''; });
    arrangeProductRows(list);
    const lines = grid.querySelectorAll('.ak-shared-six-grid-line--vertical');
    const subdivisions = lines.length / 6;
    if (!Number.isInteger(subdivisions) || !subdivisions) return;
    const rect = list.getBoundingClientRect();
    const ratio = devicePixelRatio || 1;
    const snap = value => Math.round(value * ratio) / ratio;
    // Removed opening grids no longer provide measurable cell positions.
    // Their three construction strokes use the same master quarters.
    const boundaries = grid.getBoundingClientRect().width > 0
      ? [0, ...[1, 3, 5].map(field =>
          lines[field * subdivisions].getBoundingClientRect().left - rect.left
        ), rect.width]
      : [0, rect.width / 4, rect.width / 2, rect.width * 3 / 4, rect.width];
    const widths = [];
    boundaries.slice(1).forEach((right, i) => {
      const left = boundaries[i];
      let previous = left;
      for (let step = 1; step <= 15; step++) {
        const edge = step === 15 ? right : snap(rect.left + left + (right - left) * step / 15) - rect.left;
        widths.push(`${edge - previous}px`);
        previous = edge;
      }
    });
    const pagination = document.querySelector('#filterable .ak-pagination');
    if (pagination) {
      const paginationRect = pagination.getBoundingClientRect();
      const first = rect.left + boundaries[1] - paginationRect.left;
      const last = rect.left + boundaries[3] - paginationRect.left;
      const paginationTracks = `${first}px ${last - first}px ${paginationRect.width - last}px`;
      if (pagination.style.getPropertyValue('--ak-pagination-tracks') !== paginationTracks) {
        pagination.style.setProperty('--ak-pagination-tracks', paginationTracks);
      }
    }
    const tracks = widths.join(' ');
    if (list.style.getPropertyValue('--ak-collection-tracks') !== tracks) {
      list.style.setProperty('--ak-collection-tracks', tracks);
    }
  }
  const grid = document.querySelector('.ak-top-collection-desktop__grid, .ak-collection-editorial-divider .ak-vpcompress22-desktop__grid');
  if (grid) new MutationObserver(queue).observe(grid, {childList:true, subtree:true});
  const results = document.querySelector('#results');
  if (results) new MutationObserver(queue).observe(results, {childList:true, subtree:true});
  window.addEventListener('resize', queue, {passive:true});
  window.addEventListener('pageshow', queue);
  document.addEventListener('shopify:section:load', queue);
  // Scrolling beneath a stationary mouse must not activate successive cards.
  const hoverRoot = document.querySelector('#filterable');
  if (hoverRoot) {
    let pointer = null;
    let lastScroll = -Infinity;
    const suspendHover = () => {
      lastScroll = performance.now();
      if (!hoverRoot.hasAttribute('data-ak-scroll-hover-paused')) {
        hoverRoot.setAttribute('data-ak-scroll-hover-paused', '');
      }
    };
    window.addEventListener('wheel', suspendHover, {passive: true});
    window.addEventListener('scroll', suspendHover, {passive: true});
    window.addEventListener('touchmove', suspendHover, {passive: true});
    window.addEventListener('pointermove', event => {
      if (event.pointerType !== 'mouse') return;
      const moved = pointer && (event.clientX !== pointer.x || event.clientY !== pointer.y);
      pointer = {x: event.clientX, y: event.clientY};
      if (moved && performance.now() - lastScroll > 100) {
        hoverRoot.removeAttribute('data-ak-scroll-hover-paused');
      }
    }, {passive: true});
  }
  queue();
})();
